import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { type ChatSession, useChatSessions, useUpdateChatSession } from '@/hooks/useChatSessions'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth-provider'
import { useSidebar } from '@/providers/sidebar-provider'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  BarChart3,
  Boxes,
  CalendarClock,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  Database,
  FileText,
  Headphones,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MessageSquare,
  PackageCheck,
  PhoneCall,
  Settings,
  Stethoscope,
  Upload,
  Users,
  X,
} from 'lucide-react'
import * as React from 'react'

const mainNavigation = [
  { name: 'Home', href: '/', icon: LayoutDashboard },
  { name: 'Patients', href: '/patients', icon: Users },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Appointments', href: '/appointments', icon: CalendarClock },
  { name: 'Roster', href: '/roster', icon: Stethoscope },
  { name: 'Calendar', href: '/calendar', icon: CalendarDays },
  { name: 'Stock', href: '/stock', icon: Boxes },
  { name: 'Packages', href: '/packages', icon: PackageCheck },
  { name: 'Follow-ups', href: '/follow-ups', icon: PhoneCall },
  // Redemption (invoice-reconstruction) deprecated Jul 10, 2026 — inaccurate per-patient.
  // Superseded by /packages (real Avixo dispense-ledger balance). Removed from nav;
  // route kept with a deprecation banner. Delete in a later cleanup PR.
  // { name: 'Redemption', href: '/redemption', icon: PackageCheck },
  { name: 'Treatments', href: '/treatments', icon: Stethoscope },
]

const toolsNavigation = [
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Knowledge Base', href: '/knowledge-base', icon: Database },
  { name: 'Upload Data', href: '/upload', icon: Upload },
]

// WhatsApp sub-navigation
const waSubNavigation = [
  { name: 'Conversations', href: '/whatsapp' },
  { name: 'Templates', href: '/whatsapp-templates' },
  { name: 'Audiences', href: '/whatsapp-audiences' },
  { name: 'Campaigns', href: '/whatsapp-campaigns' },
  { name: 'Do Not Blast', href: '/whatsapp-optouts' },
  { name: 'Flow Responses', href: '/whatsapp-flow-responses' },
]

const adminNavigation = [
  { name: 'Support', href: '/support', icon: Headphones },
  { name: 'Settings', href: '/settings', icon: Settings },
]

// Shared sidebar content component for both desktop and mobile
interface SidebarContentProps {
  isCollapsed: boolean
  isMobile?: boolean
  onNavigate?: () => void
}

function SidebarContent({ isCollapsed, isMobile = false, onNavigate }: SidebarContentProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { toggle } = useSidebar()
  const { user, logout } = useAuth()
  const [isChatExpanded, setIsChatExpanded] = React.useState(false)
  const [isWAExpanded, setIsWAExpanded] = React.useState(false)
  const [editingSessionId, setEditingSessionId] = React.useState<number | null>(null)
  const [editTitle, setEditTitle] = React.useState('')
  const editInputRef = React.useRef<HTMLInputElement>(null)

  // Fetch recent chat sessions (limit 3) - must include userId for query to run
  const { data: sessionsData } = useChatSessions({ limit: 3, userId: user?.id?.toString() })
  const recentSessions = sessionsData?.sessions ?? []
  const updateSession = useUpdateChatSession()

  // Check if we're on chat page
  const isChatActive = location.pathname === '/chat' || location.pathname.startsWith('/chat/')

  // Check if we're on WhatsApp pages
  const isWAActive = location.pathname === '/whatsapp' || location.pathname.startsWith('/whatsapp')

  // Truncate title to ~3 words with ellipsis
  const truncateTitle = (title: string, maxWords = 3): string => {
    const words = title.split(/\s+/)
    if (words.length <= maxWords) return title
    return words.slice(0, maxWords).join(' ') + '...'
  }

  // Auto-expand when on chat page
  React.useEffect(() => {
    if (isChatActive && !isCollapsed) {
      setIsChatExpanded(true)
    }
  }, [isChatActive, isCollapsed])

  // Auto-expand when on WhatsApp pages
  React.useEffect(() => {
    if (isWAActive && !isCollapsed) {
      setIsWAExpanded(true)
    }
  }, [isWAActive, isCollapsed])

  // Focus input when editing
  React.useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingSessionId])

  // Start editing a session title
  const startEditing = (session: ChatSession) => {
    setEditingSessionId(session.id)
    setEditTitle(session.title || 'Untitled chat')
  }

  // Save the edited title
  const saveEdit = () => {
    if (editingSessionId && editTitle.trim()) {
      updateSession.mutate({ sessionId: editingSessionId, title: editTitle.trim() })
    }
    setEditingSessionId(null)
    setEditTitle('')
  }

  // Cancel editing
  const cancelEdit = () => {
    setEditingSessionId(null)
    setEditTitle('')
  }

  // Handle key events
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // Handle link click on mobile (close sidebar)
  const handleLinkClick = () => {
    onNavigate?.()
  }

  return (
    <>
      {/* Logo Section - Fixed at top */}
      <div
        className={cn(
          'flex items-center py-5 transition-all duration-300 flex-shrink-0',
          isCollapsed ? 'justify-center px-3' : 'justify-between px-4'
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 transition-all duration-300',
            isCollapsed && 'gap-0'
          )}
        >
          <div className="w-[32px] h-[32px] flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
              <path
                d="M12 2C8.5 2 6 5 6 8c0 4.5 3.5 7 6 12 2.5-5 6-7.5 6-12 0-3-2.5-6-6-6z"
                fill="currentColor"
              />
            </svg>
          </div>
          {!isCollapsed && (
            <span className="text-2xl font-bold tracking-tight whitespace-nowrap">NurinV2</span>
          )}
        </div>
        {/* Desktop only toggle - hide on mobile */}
        {!isMobile && (
          <button
            onClick={toggle}
            className={cn(
              'w-8 h-8 rounded-full border border-border bg-card flex items-center justify-center hover:bg-hover transition-all duration-300',
              isCollapsed && 'absolute -right-3 top-6 shadow-md bg-card z-30'
            )}
          >
            <ChevronLeft
              className={cn(
                'w-4 h-4 text-foreground transition-transform duration-300',
                isCollapsed && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {/* Scrollable Middle Section */}
      <ScrollArea className="flex-1">
        <div className="pb-4">
          {/* Main Navigation */}
          <nav
            className={cn('space-y-1 transition-all duration-300', isCollapsed ? 'px-3' : 'px-4')}
          >
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={isCollapsed ? item.name : undefined}
                  onClick={handleLinkClick}
                  className={cn(
                    'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium',
                    isCollapsed
                      ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                      : 'gap-3 px-3.5 py-2.5 rounded-full',
                    isActive
                      ? 'bg-active text-text-dark'
                      : 'text-text-secondary hover:bg-hover hover:text-text-dark'
                  )}
                >
                  <item.icon className="w-[20px] h-[20px] flex-shrink-0 text-foreground" />
                  {!isCollapsed && <span>{item.name}</span>}
                </Link>
              )
            })}
          </nav>

          {/* Tools Section */}
          <nav className={cn('mt-6 transition-all duration-300', isCollapsed ? 'px-3' : 'px-4')}>
            {/* Separator line */}
            <div className={cn('h-px bg-border mb-4', isCollapsed ? 'mx-2' : 'mx-1')} />
            {!isCollapsed && (
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider px-3 mb-3">
                Tools
              </div>
            )}
            <div className="space-y-1">
              {/* AI Chat with expandable submenu - Google AI Studio style */}
              <div>
                <div
                  className={cn(
                    'flex items-center transition-all duration-150',
                    isCollapsed ? 'justify-center' : 'gap-0'
                  )}
                >
                  {/* Main AI Chat link - always goes to new chat */}
                  <button
                    type="button"
                    onClick={() => {
                      // Generate fresh timestamp on click, not on render
                      navigate({ to: '/chat', search: { new: Date.now().toString() } })
                      handleLinkClick()
                    }}
                    title={isCollapsed ? 'AI Chat' : undefined}
                    className={cn(
                      'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium',
                      isCollapsed
                        ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                        : 'flex-1 gap-3 px-3.5 py-2.5 rounded-full',
                      isChatActive
                        ? 'bg-active text-text-dark'
                        : 'text-text-secondary hover:bg-hover hover:text-text-dark'
                    )}
                  >
                    <MessageSquare className="w-[20px] h-[20px] flex-shrink-0 text-foreground" />
                    {!isCollapsed && <span>AI Chat</span>}
                  </button>

                  {/* Separate chevron toggle button */}
                  {!isCollapsed && (
                    <button
                      onClick={() => setIsChatExpanded(!isChatExpanded)}
                      className="flex items-center justify-center w-8 h-10 text-text-muted hover:text-text-dark transition-colors"
                    >
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform duration-200',
                          isChatExpanded && 'rotate-180'
                        )}
                      />
                    </button>
                  )}
                </div>

                {/* Chat History Submenu */}
                {!isCollapsed && isChatExpanded && (
                  <div className="mt-1 ml-3 pl-5 border-l border-border/50">
                    {/* Recent Sessions */}
                    <div className="space-y-0.5">
                      {recentSessions.map((session) => {
                        const isActiveSession = location.href.includes(`session=${session.id}`)
                        const isEditing = editingSessionId === session.id

                        if (isEditing) {
                          return (
                            <div
                              key={session.id}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-hover ring-1 ring-primary/50"
                            >
                              <Input
                                ref={editInputRef}
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                onBlur={saveEdit}
                                disabled={updateSession.isPending}
                                className="h-6 text-[13px] px-1.5 py-0.5 bg-background"
                                placeholder="Chat title..."
                              />
                              <button
                                type="button"
                                onClick={saveEdit}
                                disabled={updateSession.isPending}
                                className="p-0.5 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={updateSession.isPending}
                                className="p-0.5 rounded text-muted-foreground hover:bg-muted transition-colors"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )
                        }

                        return (
                          <Link
                            key={session.id}
                            to="/chat"
                            search={{ session: session.id.toString() }}
                            onClick={handleLinkClick}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              startEditing(session)
                            }}
                            className={cn(
                              'block px-3 py-1.5 text-[13px] text-text-secondary hover:text-text-dark hover:bg-hover rounded-lg transition-colors',
                              isActiveSession && 'bg-hover/50 text-text-dark'
                            )}
                            title={session.title || 'Untitled chat'}
                          >
                            {truncateTitle(session.title || 'Untitled chat')}
                          </Link>
                        )
                      })}

                      {recentSessions.length === 0 && (
                        <div className="px-3 py-1.5 text-[12px] text-text-muted italic">
                          No chat history yet
                        </div>
                      )}
                    </div>

                    {/* View All History */}
                    {recentSessions.length > 0 && (
                      <Link
                        to="/chat"
                        search={{ history: 'true' }}
                        onClick={handleLinkClick}
                        className="flex items-center gap-1 px-3 py-1.5 text-[13px] text-text-muted hover:text-text-dark transition-colors mt-1"
                      >
                        <span>View all history</span>
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* WhatsApp with expandable submenu */}
              <div>
                <div
                  className={cn(
                    'flex items-center transition-all duration-150',
                    isCollapsed ? 'justify-center' : 'gap-0'
                  )}
                >
                  {/* Main WhatsApp link */}
                  <Link
                    to="/whatsapp"
                    title={isCollapsed ? 'WhatsApp' : undefined}
                    onClick={handleLinkClick}
                    className={cn(
                      'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium',
                      isCollapsed
                        ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                        : 'flex-1 gap-3 px-3.5 py-2.5 rounded-full',
                      isWAActive
                        ? 'bg-active text-text-dark'
                        : 'text-text-secondary hover:bg-hover hover:text-text-dark'
                    )}
                  >
                    <MessageCircle className="w-[20px] h-[20px] flex-shrink-0 text-foreground" />
                    {!isCollapsed && <span>WhatsApp</span>}
                  </Link>

                  {/* Separate chevron toggle button */}
                  {!isCollapsed && (
                    <button
                      onClick={() => setIsWAExpanded(!isWAExpanded)}
                      className="flex items-center justify-center w-8 h-10 text-text-muted hover:text-text-dark transition-colors"
                    >
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform duration-200',
                          isWAExpanded && 'rotate-180'
                        )}
                      />
                    </button>
                  )}
                </div>

                {/* WhatsApp Submenu */}
                {!isCollapsed && isWAExpanded && (
                  <div className="mt-1 ml-3 pl-5 border-l border-border/50">
                    <div className="space-y-0.5">
                      {waSubNavigation.map((item) => {
                        const isActiveSubItem = location.pathname === item.href
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={handleLinkClick}
                            className={cn(
                              'block px-3 py-1.5 text-[13px] text-text-secondary hover:text-text-dark hover:bg-hover rounded-lg transition-colors',
                              isActiveSubItem && 'bg-hover/50 text-text-dark'
                            )}
                          >
                            {item.name}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Other tools */}
              {toolsNavigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={isCollapsed ? item.name : undefined}
                    onClick={handleLinkClick}
                    className={cn(
                      'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium',
                      isCollapsed
                        ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                        : 'gap-3 px-3.5 py-2.5 rounded-full',
                      isActive
                        ? 'bg-active text-text-dark'
                        : 'text-text-secondary hover:bg-hover hover:text-text-dark'
                    )}
                  >
                    <item.icon className="w-[20px] h-[20px] flex-shrink-0 text-foreground" />
                    {!isCollapsed && <span>{item.name}</span>}
                  </Link>
                )
              })}
            </div>
          </nav>

          {/* Administration Section */}
          <nav
            className={cn('mt-6 pb-6 transition-all duration-300', isCollapsed ? 'px-3' : 'px-4')}
          >
            {/* Separator line */}
            <div className={cn('h-px bg-border mb-4', isCollapsed ? 'mx-2' : 'mx-1')} />
            {!isCollapsed && (
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider px-3 mb-3">
                Administration
              </div>
            )}
            <div className="space-y-1">
              {adminNavigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={isCollapsed ? item.name : undefined}
                    onClick={handleLinkClick}
                    className={cn(
                      'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium',
                      isCollapsed
                        ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                        : 'gap-3 px-3.5 py-2.5 rounded-full',
                      isActive
                        ? 'bg-active text-text-dark'
                        : 'text-text-secondary hover:bg-hover hover:text-text-dark'
                    )}
                  >
                    <item.icon className="w-[20px] h-[20px] flex-shrink-0 text-foreground" />
                    {!isCollapsed && <span>{item.name}</span>}
                  </Link>
                )
              })}

              {/* User Item */}
              {user && (
                <div
                  className={cn(
                    'flex items-center transition-all duration-150',
                    isCollapsed
                      ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                      : 'gap-3 px-3.5 py-2.5 rounded-full'
                  )}
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-text-dark truncate">
                        {user.name}
                      </div>
                      <div className="text-[11px] text-text-muted truncate capitalize">
                        {user.role.replace('_', ' ')}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sign Out */}
              <button
                onClick={logout}
                title={isCollapsed ? 'Sign Out' : undefined}
                className={cn(
                  'flex items-center cursor-pointer transition-all duration-150 text-[14px] font-medium text-text-secondary hover:bg-hover hover:text-destructive',
                  isCollapsed
                    ? 'justify-center w-10 h-10 rounded-xl mx-auto'
                    : 'w-full gap-3 px-3.5 py-2.5 rounded-full'
                )}
              >
                <LogOut className="w-[20px] h-[20px] flex-shrink-0" />
                {!isCollapsed && <span>Sign Out</span>}
              </button>
            </div>
          </nav>
        </div>
      </ScrollArea>
    </>
  )
}

// Main Sidebar export - handles both desktop and mobile
export function Sidebar() {
  const { isCollapsed, isMobile, openMobile, setOpenMobile } = useSidebar()

  return (
    <>
      {/* Desktop Sidebar - Fixed, hidden on mobile */}
      <aside
        className={cn(
          'hidden lg:flex flex-col bg-card fixed h-screen transition-all duration-300 ease-in-out z-20',
          isCollapsed ? 'w-[68px]' : 'w-[266px]'
        )}
      >
        <SidebarContent isCollapsed={isCollapsed} isMobile={false} />
      </aside>

      {/* Mobile Sidebar - Sheet/Drawer */}
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="w-[280px] p-0 bg-card border-r border-border">
          <div className="flex flex-col h-full">
            <SidebarContent
              isCollapsed={false}
              isMobile={true}
              onNavigate={() => setOpenMobile(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
